import { useMemo } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { useRouter } from 'next/router'
import { Asset, createAsset, SmpteTimestamp, UpNextConfig } from '@/organisms/AngelPlayer'
import { EngagementDetail } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { GetEpisodeResponse } from '@/services/ProjectsService'
import { GetEpisodeByGuidQuery } from '@/types/codegen-federation'
import { buildLinkToEpisode } from '@/utils/EpisodeUtil'
import { isSeries } from '@/utils/project-utils/project-utils'

export interface UseEpisodeAssetConfig {
  momentsEnabled?: boolean
  defaultEngagementDetail?: Maybe<EngagementDetail>
}

export const usePureEpisodeAsset = (data?: GetEpisodeByGuidQuery): { asset?: Asset } => {
  const { query } = useRouter()

  return useMemo(() => {
    const episode = data?.episode
    if (!episode) return { asset: undefined }

    const smpte = query.smpte as SmpteTimestamp
    const startTime = query?.position ? parseInt(query.position as string) : 0
    const position = smpte ?? startTime ?? 0

    const asset = createAsset({
      guid: episode.guid as string,
      projectSlug: episode.projectSlug as string,
      slug: episode.slug as string,
      name: episode.name as string,
      url: episode.source?.url as string,
      title: episode.subtitle as string,
      subtitle: episode.subtitle as string,
      description: episode.description as string,
      position,
      episodeNumber: episode.episodeNumber as number,
      releaseDate: episode.releaseDate,
      seasonId: episode.seasonId as string,
      duration: episode.source?.duration as number,
      videoType: 'episode',
      credits: episode.source?.credits as number,
    })

    return {
      asset,
    }
  }, [data?.episode, query])
}

export const useEpisodeAsset = (
  episodeResponse?: GetEpisodeResponse,
  config?: UseEpisodeAssetConfig,
): { asset?: Asset } => {
  const { push, query } = useRouter()
  const { episode, project } = episodeResponse || {}

  const asset = useMemo(() => {
    if (!episode || !project) return

    let upNextMetadata: UpNextConfig | undefined

    if (episode.upNext?.guid && project.slug && isSeries(project)) {
      upNextMetadata = {
        goToNext: () => {
          push(buildLinkToEpisode(episode.upNext, project.slug))
        },
        showTime: Math.min(
          episode.source.credits ? episode.source.credits + 8 : Number.POSITIVE_INFINITY,
          episode.source.duration - 15,
        ),
        autoGoToNextAfterMs: 10000,
      }
    }

    const smpte = query.smpte as SmpteTimestamp
    const startTime = query.position ? parseInt(query.position as string) : undefined
    const position =
      smpte ?? startTime ?? config?.defaultEngagementDetail?.startTime ?? episode?.watchPosition?.position ?? 0

    return createAsset({
      guid: episode.guid,
      publisherName: project.publisherName,
      projectSlug: project.slug,
      projectName: project.name,
      slug: episode.slug,
      name: episode.name,
      url: episode.source.url,
      title: episode.subtitle,
      subtitle: episode.subtitle,
      description: episode.description,
      position,
      upNext: upNextMetadata,
      episodeNumber: episode.episodeNumber,
      releaseDate: episode.releaseDate,
      seasonId: episode.seasonId,
      duration: episode.source.duration,
      videoType: 'episode',
      credits: episode.source.credits,
      vmapUrl: episode.vmapUrl,
    })
  }, [episode, project, config?.defaultEngagementDetail?.startTime, push, query.position, query.smpte])

  return { asset }
}
